/**
 * voiceStatsSlice.js
 *
 * Phase 4a — read-only client state backing the Book Creator's voice-
 * readiness UI. Consumes the three endpoints defined in
 * BOOK_VOICE_STATS_SPEC §2, keyed by bookId / litGroupId, with a per-thunk
 * 30-second TTL (spec §5.2 / §9.2) so tight remount loops on the worksheet
 * page don't pound the aggregates.
 *
 * Deliberately separate from voiceCloningSlice: cloning is action-heavy
 * (seed / retry / smokeTest / delete) whereas stats is pure read. Mixing
 * them would muddy memoization and single-responsibility (spec §5).
 *
 * Thunks:
 *   - fetchBookVoiceStats({ bookId, include?, force? })
 *   - fetchBookScriptCoverage({ bookId, scriptVersion?, fileName?, force? })
 *   - fetchLitGroupVoiceStats({ litGroupId, include?, force? })
 *
 * Reducer actions (exported):
 *   - invalidateBookVoiceStats({ bookId }) — clears stats + coverage caches
 *     for that book; consumed by chunk 3's clone-complete wiring to force a
 *     refetch after VoiceCloningService flips a clone to `success`.
 *   - setActiveScriptVersion({ bookId, scriptVersion })
 *     — stores the user's <ScriptVersionSelector> pick, nulls cached
 *       coverage so the next selector call refetches against the new
 *       version.
 *   - clearVoiceStatsError({ bookId?, litGroupId?, scope? })
 *
 * Selectors per spec §5.3 — plain (not reselect) to mirror the style in
 * voiceCloningSlice.js; memoize at the call site only if a derived
 * shape is needed.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../constants/config';

// ---------------------------------------------------------------------------
// Shared helpers (mirror voiceCloningSlice.js patterns verbatim)
// ---------------------------------------------------------------------------
const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const handle = async (response, rejectWithValue) => {
  if (!response.ok) {
    let err;
    try {
      err = await response.json();
    } catch (_) {
      err = { message: response.statusText };
    }
    return rejectWithValue(err);
  }
  return response.json();
};

// Server returns `{ success, data, error }` (API_DATA_WRAPPER). Unwrap to
// the `data` payload so reducers write the shape documented in spec §2.
const unwrap = (payload) => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data;
  }
  return payload;
};

// Thunk-condition TTL guard — short-circuits repeat fetches within the
// 30-second window unless `force: true` is passed. Spec §5.2.
const TTL_MS = 30 * 1000;
const isFresh = (lastFetchedIso) => {
  if (!lastFetchedIso) return false;
  const last = new Date(lastFetchedIso).getTime();
  return Number.isFinite(last) && (Date.now() - last) < TTL_MS;
};

const buildIncludeQuery = (include) => {
  if (!include) return '';
  const list = Array.isArray(include)
    ? include.filter(Boolean)
    : String(include).split(',').map(s => s.trim()).filter(Boolean);
  if (list.length === 0) return '';
  return `?include=${encodeURIComponent(list.join(','))}`;
};

// Lazily materialize a bookEntry — all extraReducers go through this so we
// don't repeat the defaulting shape in five places.
const ensureBookEntry = (state, bookId) => {
  if (!state.byBook[bookId]) {
    state.byBook[bookId] = {
      stats: null,
      coverage: null,
      scriptVersion: null,
      loading: { stats: false, coverage: false },
      error: { stats: null, coverage: null },
      lastFetched: { stats: null, coverage: null }
    };
  }
  return state.byBook[bookId];
};

const ensureLitGroupEntry = (state, litGroupId) => {
  if (!state.byLitGroup[litGroupId]) {
    state.byLitGroup[litGroupId] = {
      stats: null,
      loading: false,
      error: null,
      lastFetched: null
    };
  }
  return state.byLitGroup[litGroupId];
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * GET /api/books/:bookId/voice-stats?include=totals,perCharacter,samples
 * Spec §2.1. `include` may be an array or a comma-sep string.
 */
export const fetchBookVoiceStats = createAsyncThunk(
  'voiceStats/fetchBookVoiceStats',
  async ({ bookId, include } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const url =
        config.GET_BOOK_VOICE_STATS.replace(':bookId', bookId) +
        buildIncludeQuery(include);
      // eslint-disable-next-line no-console
      console.log(`[voiceStatsSlice.fetchBookVoiceStats] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceStatsSlice.fetchBookVoiceStats] bookId=${bookId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  },
  {
    condition: ({ bookId, force } = {}, { getState }) => {
      if (!bookId) return false;
      if (force) return true;
      const entry = getState().voiceStats?.byBook?.[bookId];
      if (entry?.loading?.stats) return false;
      return !isFresh(entry?.lastFetched?.stats);
    }
  }
);

/**
 * GET /api/books/:bookId/script-coverage?scriptVersion=|fileName=
 * Spec §2.2.
 */
export const fetchBookScriptCoverage = createAsyncThunk(
  'voiceStats/fetchBookScriptCoverage',
  async ({ bookId, scriptVersion, fileName } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const qs = new URLSearchParams();
      if (scriptVersion) qs.set('scriptVersion', scriptVersion);
      if (fileName) qs.set('fileName', fileName);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const url = config.GET_BOOK_SCRIPT_COVERAGE.replace(':bookId', bookId) + suffix;
      // eslint-disable-next-line no-console
      console.log(`[voiceStatsSlice.fetchBookScriptCoverage] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceStatsSlice.fetchBookScriptCoverage] bookId=${bookId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  },
  {
    // Coverage is cached per (bookId, scriptVersion|fileName). The slice
    // stashes the requested version on the book entry; if the caller asks
    // for a different one, the `coverage` cache is logically stale — so
    // we consult the stored `scriptVersion` to decide freshness.
    condition: ({ bookId, scriptVersion, fileName, force } = {}, { getState }) => {
      if (!bookId) return false;
      if (force) return true;
      const entry = getState().voiceStats?.byBook?.[bookId];
      if (entry?.loading?.coverage) return false;
      if (!isFresh(entry?.lastFetched?.coverage)) return true;
      // If the cached coverage targets a different version than requested,
      // refetch regardless of TTL.
      const cachedSV = entry?.coverage?.scriptVersion;
      if (scriptVersion && cachedSV?.uuid !== scriptVersion && cachedSV?.version !== String(scriptVersion)) {
        return true;
      }
      if (fileName && cachedSV?.fileName !== fileName) {
        return true;
      }
      return false;
    }
  }
);

/**
 * GET /api/litgroups/:litGroupId/voice-stats?include=totals,perBook,sharedCharacters
 * Spec §2.3.
 */
export const fetchLitGroupVoiceStats = createAsyncThunk(
  'voiceStats/fetchLitGroupVoiceStats',
  async ({ litGroupId, include } = {}, { rejectWithValue }) => {
    if (!litGroupId) return rejectWithValue({ message: 'litGroupId required' });
    try {
      const url =
        config.GET_LITGROUP_VOICE_STATS.replace(':litGroupId', litGroupId) +
        buildIncludeQuery(include);
      // eslint-disable-next-line no-console
      console.log(`[voiceStatsSlice.fetchLitGroupVoiceStats] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceStatsSlice.fetchLitGroupVoiceStats] litGroupId=${litGroupId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  },
  {
    condition: ({ litGroupId, force } = {}, { getState }) => {
      if (!litGroupId) return false;
      if (force) return true;
      const entry = getState().voiceStats?.byLitGroup?.[litGroupId];
      if (entry?.loading) return false;
      return !isFresh(entry?.lastFetched);
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const initialState = {
  byBook: {},       // { [bookId]: { stats, coverage, scriptVersion, loading, error, lastFetched } }
  byLitGroup: {}    // { [litGroupId]: { stats, loading, error, lastFetched } }
};

const slice = createSlice({
  name: 'voiceStats',
  initialState,
  reducers: {
    /**
     * Drop cached stats + coverage for a book so the next thunk call (after
     * the 30s TTL guard is bypassed by the cleared `lastFetched`) refetches.
     * Chunk 3 dispatches this on clone-complete so BookVoiceStatsStrip
     * updates without a page reload (spec §8.1 item 3 / §9.2).
     *
     * `action.payload` may be `{ bookId }` or just the raw bookId string.
     * If bookId is omitted, the entire byBook map is cleared.
     */
    invalidateBookVoiceStats(state, action) {
      const payload = action.payload;
      const bookId = typeof payload === 'string'
        ? payload
        : (payload && payload.bookId) || null;
      if (!bookId) {
        state.byBook = {};
        return;
      }
      if (state.byBook[bookId]) {
        state.byBook[bookId].stats = null;
        state.byBook[bookId].coverage = null;
        state.byBook[bookId].lastFetched = { stats: null, coverage: null };
        state.byBook[bookId].error = { stats: null, coverage: null };
      }
    },

    /**
     * Drop cached stats for a LitGroup. Used when a book in the series has
     * a clone-complete; series totals are affected.
     */
    invalidateLitGroupVoiceStats(state, action) {
      const payload = action.payload;
      const litGroupId = typeof payload === 'string'
        ? payload
        : (payload && payload.litGroupId) || null;
      if (!litGroupId) {
        state.byLitGroup = {};
        return;
      }
      if (state.byLitGroup[litGroupId]) {
        state.byLitGroup[litGroupId].stats = null;
        state.byLitGroup[litGroupId].lastFetched = null;
        state.byLitGroup[litGroupId].error = null;
      }
    },

    /**
     * Records the user's <ScriptVersionSelector> pick for a book. Clears
     * the cached coverage for that book so the next fetchBookScriptCoverage
     * call refetches against the newly-selected version (spec §7.4).
     *
     * payload = { bookId, scriptVersion: { uuid, fileName, version } | null }
     */
    setActiveScriptVersion(state, action) {
      const { bookId, scriptVersion } = action.payload || {};
      if (!bookId) return;
      const entry = ensureBookEntry(state, bookId);
      entry.scriptVersion = scriptVersion || null;
      // The cached coverage targeted a (possibly) different version — flush it.
      entry.coverage = null;
      entry.lastFetched.coverage = null;
      entry.error.coverage = null;
    },

    /**
     * Banner dismissal / error-clear helper.
     * payload = { bookId?, litGroupId?, scope?: 'stats'|'coverage'|'all' }
     */
    clearVoiceStatsError(state, action) {
      const { bookId, litGroupId, scope = 'all' } = action.payload || {};
      if (bookId && state.byBook[bookId]) {
        if (scope === 'all' || scope === 'stats') {
          state.byBook[bookId].error.stats = null;
        }
        if (scope === 'all' || scope === 'coverage') {
          state.byBook[bookId].error.coverage = null;
        }
      }
      if (litGroupId && state.byLitGroup[litGroupId]) {
        state.byLitGroup[litGroupId].error = null;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchBookVoiceStats --------------------------------------------
      .addCase(fetchBookVoiceStats.pending, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.stats = true;
        entry.error.stats = null;
      })
      .addCase(fetchBookVoiceStats.fulfilled, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.stats = false;
        entry.error.stats = null;
        entry.stats = action.payload || null;
        entry.lastFetched.stats = new Date().toISOString();
      })
      .addCase(fetchBookVoiceStats.rejected, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.stats = false;
        entry.error.stats =
          action.payload?.message ||
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load voice stats';
      })

      // fetchBookScriptCoverage ----------------------------------------
      .addCase(fetchBookScriptCoverage.pending, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.coverage = true;
        entry.error.coverage = null;
      })
      .addCase(fetchBookScriptCoverage.fulfilled, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.coverage = false;
        entry.error.coverage = null;
        entry.coverage = action.payload || null;
        entry.lastFetched.coverage = new Date().toISOString();
        // Keep scriptVersion in sync with whatever the server actually served
        // (caller may have passed nothing and received the latest).
        if (action.payload?.scriptVersion) {
          entry.scriptVersion = action.payload.scriptVersion;
        }
      })
      .addCase(fetchBookScriptCoverage.rejected, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.coverage = false;
        entry.error.coverage =
          action.payload?.message ||
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load script coverage';
      })

      // fetchLitGroupVoiceStats ----------------------------------------
      .addCase(fetchLitGroupVoiceStats.pending, (state, action) => {
        const { litGroupId } = action.meta.arg || {};
        if (!litGroupId) return;
        const entry = ensureLitGroupEntry(state, litGroupId);
        entry.loading = true;
        entry.error = null;
      })
      .addCase(fetchLitGroupVoiceStats.fulfilled, (state, action) => {
        const { litGroupId } = action.meta.arg || {};
        if (!litGroupId) return;
        const entry = ensureLitGroupEntry(state, litGroupId);
        entry.loading = false;
        entry.error = null;
        entry.stats = action.payload || null;
        entry.lastFetched = new Date().toISOString();
      })
      .addCase(fetchLitGroupVoiceStats.rejected, (state, action) => {
        const { litGroupId } = action.meta.arg || {};
        if (!litGroupId) return;
        const entry = ensureLitGroupEntry(state, litGroupId);
        entry.loading = false;
        entry.error =
          action.payload?.message ||
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load LitGroup voice stats';
      });
  }
});

export const {
  invalidateBookVoiceStats,
  invalidateLitGroupVoiceStats,
  setActiveScriptVersion,
  clearVoiceStatsError
} = slice.actions;

// ---------------------------------------------------------------------------
// Selectors (spec §5.3)
// ---------------------------------------------------------------------------

const root = (state) => state.voiceStats || initialState;
const bookEntry = (state, bookId) => (bookId ? root(state).byBook[bookId] : null);
const lgEntry = (state, litGroupId) =>
  (litGroupId ? root(state).byLitGroup[litGroupId] : null);

export const selectBookVoiceStats = (state, bookId) =>
  bookEntry(state, bookId)?.stats || null;

export const selectBookScriptCoverage = (state, bookId) =>
  bookEntry(state, bookId)?.coverage || null;

export const selectBookVoiceStatsLoading = (state, bookId) => {
  const e = bookEntry(state, bookId);
  return {
    stats: !!e?.loading?.stats,
    coverage: !!e?.loading?.coverage
  };
};

export const selectBookVoiceStatsError = (state, bookId) => {
  const e = bookEntry(state, bookId);
  return {
    stats: e?.error?.stats || null,
    coverage: e?.error?.coverage || null
  };
};

export const selectBookVoiceStatsLastFetched = (state, bookId) => {
  const e = bookEntry(state, bookId);
  return {
    stats: e?.lastFetched?.stats || null,
    coverage: e?.lastFetched?.coverage || null
  };
};

/**
 * The user's <ScriptVersionSelector> pick for a book. Null until the
 * selector dispatches setActiveScriptVersion, at which point chunk 3 will
 * prefer it over the server's "latest" default.
 */
export const selectActiveScriptVersion = (state, bookId) =>
  bookEntry(state, bookId)?.scriptVersion || null;

export const selectLitGroupVoiceStats = (state, litGroupId) =>
  lgEntry(state, litGroupId)?.stats || null;

export const selectLitGroupVoiceStatsLoading = (state, litGroupId) =>
  !!lgEntry(state, litGroupId)?.loading;

export const selectLitGroupVoiceStatsError = (state, litGroupId) =>
  lgEntry(state, litGroupId)?.error || null;

export default slice.reducer;
